import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SyncOutlined } from '@ant-design/icons'
import { RadioChangeEvent } from 'antd'
import { boolean } from 'yup'

import {
  BenefitProductClassResponse,
  NamedItem,
  PerksClassTypes,
  deleteCustomClassType,
  saveCustomClassType,
} from 'core'
import PerkClassTypeId from 'core/domain/benefit/perkClassTypeIds'

import useCommandRequest from 'common/hooks/useCommandRequest'

import { Button, Col, DefaultInput, Radio, Row, Tag } from 'common/components'

type SaveProductClassFormProps = {
  initialValues: SaveSingleProductClassFormProps
  productClasses: BenefitProductClassResponse[]
  classTypes: PerksClassTypes[]
  onChangeProductClassValues: React.Dispatch<React.SetStateAction<any[]>>
  onProductClassSaved(): void
}

export type SaveSingleProductClassFormProps = {
  id?: string
  classTypeId?: number
  name?: string
  planPeriodId: string
}

export const ProductClassForm = (props: SaveProductClassFormProps) => {
  const { productClasses, classTypes, onChangeProductClassValues, initialValues, onProductClassSaved } = props
  const { t: translate } = useTranslation()
  const [selectedClassesOption, setSelectedClassesOption] = useState(null)
  const [isAddingClassTypeLoading, setIsAddingClassTypeLoading] = useState(false)
  const [isDeletingClassTypeLoading, setIsDeletingClassTypeLoading] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [sendCommand, isLoading] = useCommandRequest<string>()
  const [sendDeleteCommand] = useCommandRequest<string>()
  const [selectedId, setSelectedId] = useState(0)

  const hasAllEligibleEmployees = productClasses
    .map((c) => c.classTypeId)
    .includes(PerkClassTypeId.AllEligibleEmployeesId)

  const handleInputChange = (e) => setInputValue(e.currentTarget.value)

  const handleAddProductClass = async (item: NamedItem) => {
    setIsAddingClassTypeLoading(true)
    onChangeProductClassValues((prevState) => [...prevState, { classTypeId: item.id, name: item.name }])

    setSelectedId(item.id as number)
    setIsAddingClassTypeLoading(false)
  }

  const handleDeleteProductClass = async (selectedClass: BenefitProductClassResponse) => {
    setSelectedId(selectedClass.classTypeId)
    setIsDeletingClassTypeLoading(true)
    const res = await sendDeleteCommand(() =>
      deleteCustomClassType({
        planPeriodId: props.initialValues.planPeriodId,
        customClassTypeId: selectedClass.classTypeId,
      }),
    )
    if (res.completed && !res.hasErrors) {
      onChangeProductClassValues((prevState) => prevState.filter((x) => x.classTypeId !== selectedClass.classTypeId))
      onProductClassSaved()
    }
    setIsDeletingClassTypeLoading(false)
  }

  useEffect(() => {
    if (productClasses?.length > 0 && classTypes?.length > 0 && selectedClassesOption === null) {
      if (hasAllEligibleEmployees) setSelectedClassesOption(0)
      else setSelectedClassesOption(1)
    }
  }, [hasAllEligibleEmployees, productClasses, classTypes, selectedClassesOption])

  const handleOptionChange = useCallback(
    async (event: RadioChangeEvent) => {
      const value = event.target.value
      if (value === 0 && !hasAllEligibleEmployees) {
        onChangeProductClassValues([
          {
            classTypeId: PerkClassTypeId.AllEligibleEmployeesId,
            name: classTypes.find((x) => x.id === PerkClassTypeId.AllEligibleEmployeesId).name,
          },
        ])
      }
      if (value === 1 && hasAllEligibleEmployees) {
        onChangeProductClassValues((prevState) =>
          prevState.filter((x) => x.classTypeId !== PerkClassTypeId.AllEligibleEmployeesId),
        )
      }
      setSelectedClassesOption(value)
    },
    [hasAllEligibleEmployees, classTypes, productClasses],
  )
  return (
    classTypes && (
      <Row className="mb-12">
        <Col xs={24} md={12}>
          <Row className="mb-24">
            <Radio.Group
              name="productClasses"
              value={selectedClassesOption}
              onChange={handleOptionChange}
              className={'w-100'}
              size={'large'}
            >
              <Row>
                <Radio.Button value={0} className="w-100 mb-12">
                  {translate(
                    'admin-dashboard.benefit-details.perks-benefit-create-eligible-classes.form-all-employees',
                  )}
                </Radio.Button>
              </Row>
              <Row>
                <Radio.Button value={1} className="w-100">
                  {translate(
                    'admin-dashboard.benefit-details.perks-benefit-create-eligible-classes.form-select-classes',
                  )}
                </Radio.Button>
              </Row>
            </Radio.Group>
          </Row>
          {selectedClassesOption === 1 && (
            <div>
              <div className="mb-24">
                <div className="mb-8">
                  {translate('admin-dashboard.benefit-details.perks-benefit-create-eligible-classes.form-add-class')}
                </div>
                <DefaultInput
                  name={'classtype'}
                  className="mb-4"
                  placeholder={translate(
                    'admin-dashboard.benefit-details.perks-benefit-create-eligible-classes.form-input-placeholder',
                  )}
                  disabled={isLoading}
                  value={inputValue}
                  onChange={handleInputChange}
                  onPressEnter={async (e) => {
                    const value = e.currentTarget.value
                    e.stopPropagation()
                    e.preventDefault()

                    const result = await sendCommand(() =>
                      saveCustomClassType({
                        planPeriodId: props.initialValues.planPeriodId,
                        name: value,
                      }),
                    )
                    if (result.completed) {
                      handleAddProductClass({ name: value, id: result.data })
                      setInputValue('')
                    }
                  }}
                />

                <div>
                  {productClasses
                    .filter((x) => x.classTypeId !== classTypes[0].id)
                    .map((c) => (
                      <span className="inline-flex mb-8">
                        <Tag
                          color="#11D175"
                          closable
                          icon={
                            c.classTypeId === selectedId && isDeletingClassTypeLoading ? <SyncOutlined spin /> : null
                          }
                          key={c.id}
                          onClose={(e) => {
                            e.preventDefault()
                            handleDeleteProductClass(c)
                          }}
                        >
                          {c.name}
                        </Tag>
                      </span>
                    ))}
                </div>
              </div>
              <div>
                <div className="color-gray-7 mb-8">
                  {translate(
                    'admin-dashboard.benefit-details.perks-benefit-create-eligible-classes.form-suggested-perks',
                  )}
                </div>
                <div>
                  {classTypes
                    .filter(
                      (x) =>
                        x.id !== classTypes[0]?.id &&
                        !productClasses.map((c) => c.classTypeId).includes(x.id as number),
                    )
                    .map((type) => (
                      <Button
                        type="default"
                        icon="fa fa-plus"
                        className="mb-8 mr-8"
                        loading={type.id === selectedId && isAddingClassTypeLoading}
                        onClick={() => handleAddProductClass(type)}
                      >
                        {type.name}
                      </Button>
                    ))}
                </div>
              </div>
            </div>
          )}
        </Col>
      </Row>
    )
  )
}
